/** The token a user writes to place the mailbox's signature themselves. */
export const SIGNATURE_TOKEN = "{signature}";

/** `{signature|Label}`, the labelled form the editors insert. */
const LABELLED_SIGNATURE_TOKEN = /\{signature\|[^{}]*\}/g;

/** A body that ends with one of these closes its own block, so one line break separates it. */
const ENDS_WITH_BLOCK = /<\/(p|div|ul|ol|table|blockquote|h[1-6])>\s*$/i;

const HTML_ENTITIES: Record<string, string> = {
	"&nbsp;": " ",
	"&amp;": "&",
	"&lt;": "<",
	"&gt;": ">",
	"&quot;": '"',
	"&#39;": "'",
};

/** Turns `{signature|Label}` into `{signature}`, so one token shape is left to place. */
export const normaliseSignatureTokens = (html: string): string =>
	html.replace(LABELLED_SIGNATURE_TOKEN, SIGNATURE_TOKEN);

const normaliseForSignatureMatch = (html: string): string =>
	html
		.replace(/<[^>]*>/g, " ")
		.replace(/&(nbsp|amp|lt|gt|quot|#39);/gi, (entity) => HTML_ENTITIES[entity.toLowerCase()])
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase();

const bodyContainsSignature = (body: string, signature: string): boolean => {
	const needle = normaliseForSignatureMatch(signature);
	const text = normaliseForSignatureMatch(body.replaceAll(SIGNATURE_TOKEN, " "));

	return needle.length > 0 && ` ${text} `.includes(` ${needle} `);
};

/**
 * The web twin of the API's `applyEmailSignature` (apps/api, schedule/utils/email-signature.util.ts),
 * so the draft preview shows what the send will do (PREC-4401). Keep the two in step.
 *
 * - The signature is already in the body: nothing is added, and `{signature}` tokens are removed.
 * - The body has `{signature}`: the signature goes where the first token is, whatever `include`
 *   says, and further tokens are removed.
 * - Otherwise, when `include` is on and the mailbox has a signature, it is appended below the body.
 */
export const placeEmailSignature = (
	body: string,
	signature: string | null | undefined,
	include: boolean
): string => {
	const ownSignature = signature ?? "";

	if (ownSignature.trim() && bodyContainsSignature(body, ownSignature)) {
		return body.replaceAll(SIGNATURE_TOKEN, "");
	}

	const tokenAt = body.indexOf(SIGNATURE_TOKEN);

	if (tokenAt !== -1) {
		const before = body.slice(0, tokenAt);
		const after = body.slice(tokenAt + SIGNATURE_TOKEN.length).replaceAll(SIGNATURE_TOKEN, "");

		return `${before}${ownSignature}${after}`;
	}

	if (!include || !ownSignature.trim()) {
		return body;
	}

	if (!body.trim()) {
		return ownSignature;
	}

	return `${body}${ENDS_WITH_BLOCK.test(body) ? "<br>" : "<br><br>"}${ownSignature}`;
};
